import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Nps } from '@app/@shared/nps/nps-loader.component';
import { BaseService, HttpService } from '@app/core';
import { I18nService } from '@app/core/i18n.service';
import { AlertDialogComponent } from '@app/dialogs/alert-dialog/alert-dialog.component';
import { ProfileConfirmationDialogComponent } from '@app/dialogs/profile-confirmation-dialog.ts/profile-confirmation-dialog.component';
import { CourseMessage, ID } from '@app/models';
import { BasicSettings } from '@app/models/core/general';
import { SCData } from '@app/models/core/SCData';
import { environment } from '@env/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

export interface ShellMenu extends Menu {
  id: string;
  created_at: string;
  updated_at: string;
  navigation_type: string;
  title: string;
  force_position: string;
  icon: string;
  sub_navigations: ShellMenu[];
  url_tree: string;
  order_value: number;
  display_mode: 'FIX_EXTENDED' | 'EXTENDED' | 'EXTENTABLE';
}

export interface Menu {
  title?: string;
  icon?: string;
  iconType?: 'SVG' | 'MAT' | 'FONT';
  routerLink?: string;
  isActive?: boolean;
  menu?: ShellMenu[];
  module?: string;
}

@UntilDestroy()
@Injectable()
export class ShellService extends BaseService {
  public navigationSections: ShellMenu[] = [];

  public parsedNavigationSections: ShellMenu[] = [];
  public isSubListExpanded: boolean = false;

  constructor(
    private i18nService: I18nService,
    private http: HttpService,
    private dialog: MatDialog,
  ) {
    super();
  }

  loadNavigation() {
    this.addSubscription(this.getNavigation()).subscribe((navResp) => {
      if (navResp) {
        // this.navigationSections = navResp.data.sort((a, b) => a.order_value - b.order_value)
        this.navigationSections = navResp.data;
        this.navigationSections.forEach((section) => {
          if (
            section.display_mode == 'EXTENDED' ||
            section.display_mode == 'FIX_EXTENDED'
          ) {
            this.isSubListExpanded = true;
          }
        });

        //Add Sandbox if production is false
        if (environment && environment.production === false) {
          this.navigationSections.push({
            url_tree: '/styleguide',
            created_at: '',
            display_mode: 'FIX_EXTENDED',
            force_position: 'BOTTOM',
            icon: 'css',
            id: '',
            order_value: 998,
            sub_navigations: [],
            title: 'Styleguide',
            updated_at: '',
            navigation_type: '',
            iconType: 'MAT',
            isActive: false,
            menu: undefined,
            routerLink: '/styleguide',
            module: undefined,
          });
          this.navigationSections.push({
            url_tree: '/sandbox',
            created_at: '',
            display_mode: 'FIX_EXTENDED',
            force_position: 'BOTTOM',
            icon: 'ar_stickers',
            id: '',
            order_value: 999,
            sub_navigations: [],
            title: 'Sandbox',
            updated_at: '',
            navigation_type: '',
            iconType: 'MAT',
            isActive: false,
            menu: undefined,
            routerLink: '/sandbox',
            module: undefined,
          });
        }
      }
    });
  }

  setLanguage(language: string) {
    this.i18nService.language = language;
  }

  //API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getNavigation(params?: any) {
    return <Observable<SCData<ShellMenu[]>>>(
      this.http.get(`/course-user/navigations`, { params: params })
    );
  }

  getCourseMessages(courseId: ID = 'Xzjd5xAvOE') {
    // this.openProfileConfirmationDialog(undefined);
    this.http
      .get<SCData<CourseMessage>>(`/course-user/courses/${courseId}/message`)
      .subscribe((resp) => {
        switch (resp?.data?.message_type) {
          case 'PROFILE_CONFIRMATION':
            this.openProfileConfirmationDialog(resp.data);
            break;

          default:
            if (resp?.data?.content) {
              this.openMessageDialog(resp.data);
            }
            break;
        }
      });
  }

  private confirmMessage(messageId: ID) {
    this.addSubscription(
      this.http.patch(`/course-user/messages/${messageId}/confirm`, {}),
    ).subscribe(() => {});
  }

  openMessageDialog(message: CourseMessage) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: message.title,
        body: message.content,
        actions: [
          {
            index: 'cancel',
            label: 'cancel',
          },
          {
            index: 'read',
            label:
              message.needs_reading_confirmation &&
              message.reading_confirmation_text
                ? message.reading_confirmation_text
                : 'course_message_read',
            color: 'primary',
          },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.action.index === 'read') {
        this.confirmMessage(message.id);
      }
    });
  }

  openProfileConfirmationDialog(message: CourseMessage | undefined) {
    const dialogRef = this.dialog.open(ProfileConfirmationDialogComponent, {
      data: {
        message: message,
      },
    });

    this.addSubscription(dialogRef.afterClosed()).subscribe(() => {
      // if (result.action.index === 'confirm') {
      // }
    });
  }

  loadNPSInfo() {
    return <Observable<SCData<Nps>>>this.http.get(`/course-user/nps`);
  }

  getBasicSettingsHelper() {
    return <Observable<SCData<BasicSettings>>>(
      this.http.get('/general/basic-settings')
    );
  }

  // saveText(model: Text) {
  //   return <Observable<SCData<ShellMenu>>>this.http.post(`/texts/texts`, model);
  // }
}
